import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const SEVERITY_CONFIG = {
  low: { label: 'Low', bg: '#d1fae5', color: '#065f46', dot: '#10b981', pulse: false },
  medium: { label: 'Medium', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', pulse: false },
  high: { label: 'High', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', pulse: true },
  critical: { label: 'Critical', bg: '#fce7f3', color: '#9d174d', dot: '#ec4899', pulse: true },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: '#ef4444' },
  responding: { label: 'Responding', color: '#f59e0b' },
  resolved: { label: 'Resolved', color: '#10b981' },
};

const SOSPanel = ({ alerts, onAlertUpdate, loading }) => {
  const [respondingId, setRespondingId] = useState(null);

  const activeAlerts = alerts?.filter(a => a?.sos_status !== 'resolved') || [];
  const resolvedAlerts = alerts?.filter(a => a?.sos_status === 'resolved') || [];

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short' });
  };

  const handleRespond = async (alertId) => {
    setRespondingId(alertId);
    try {
      const { error } = await supabase?.from('sos_alerts')?.update({
          sos_status: 'responding',
          responded_at: new Date()?.toISOString(),
          response_notes: 'Support team responding',
          updated_at: new Date()?.toISOString(),
        })?.eq('id', alertId);
      if (!error) onAlertUpdate?.();
    } catch (err) {
      console.error('Respond error:', err);
    } finally {
      setRespondingId(null);
    }
  };

  const handleResolve = async (alertId) => {
    setRespondingId(alertId);
    try {
      const { error } = await supabase?.from('sos_alerts')?.update({
          sos_status: 'resolved',
          resolved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })?.eq('id', alertId);
      if (!error) onAlertUpdate?.();
    } catch (err) {
      console.error('Resolve error:', err);
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* SOS Header */}
      <div className="p-4 border-b border-red-100" style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <Icon name="AlertTriangle" size={16} color="#fff" />
              </div>
              {activeAlerts?.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center text-xs font-bold text-red-900">
                  {activeAlerts?.length}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Emergency SOS Alerts</h3>
              <p className="text-xs text-red-200">Panic button activations</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-xs text-white font-medium">{activeAlerts?.length} Active</span>
          </div>
        </div>
      </div>
      {/* Alert List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-20">
            <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeAlerts?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-gray-400">
            <Icon name="ShieldCheck" size={24} className="mb-2 text-green-400" />
            <p className="text-sm text-green-600 font-medium">No active emergencies</p>
            <p className="text-xs text-gray-400 mt-0.5">All clear</p>
          </div>
        ) : (
          activeAlerts?.map(alert => {
            const sev = SEVERITY_CONFIG?.[alert?.severity] || SEVERITY_CONFIG?.high;
            const statusCfg = STATUS_CONFIG?.[alert?.sos_status] || STATUS_CONFIG?.active;
            const isProcessing = respondingId === alert?.id;

            return (
              <div
                key={alert?.id}
                className="rounded-xl border-2 overflow-hidden"
                style={{ borderColor: sev?.dot + '60', background: sev?.bg }}
              >
                {/* Alert Header */}
                <div className="px-3 py-2 flex items-center justify-between" style={{ background: sev?.dot + '20' }}>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <div className="w-2 h-2 rounded-full" style={{ background: sev?.dot }} />
                      {sev?.pulse && <div className="absolute inset-0 w-2 h-2 rounded-full animate-ping" style={{ background: sev?.dot }} />}
                    </div>
                    <span className="text-xs font-bold" style={{ color: sev?.color }}>{alert?.alert_number}</span>
                    <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: sev?.dot, color: '#fff' }}>
                      {sev?.label}
                    </span>
                  </div>
                  <span className="text-xs font-medium" style={{ color: statusCfg?.color }}>{statusCfg?.label}</span>
                </div>

                {/* Alert Body */}
                <div className="px-3 py-2">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: sev?.dot + '30' }}>
                      <Icon name={alert?.triggered_by_role === 'driver' ? 'Car' : 'User'} size={14} style={{ color: sev?.dot }} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: sev?.color }}>{alert?.triggered_by_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{alert?.triggered_by_role}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-xs text-gray-500">{formatTime(alert?.created_at)}</p>
                    </div>
                  </div>

                  {alert?.location_address && (
                    <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 rounded-lg" style={{ background: 'rgba(0,0,0,0.06)' }}>
                      <Icon name="MapPin" size={12} style={{ color: sev?.dot }} />
                      <span className="text-xs text-gray-700">{alert?.location_address}</span>
                    </div>
                  )}

                  {alert?.description && (
                    <p className="text-xs text-gray-600 mb-2 leading-relaxed">{alert?.description}</p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-2">
                    {alert?.sos_status === 'active' && (
                      <button
                        onClick={() => handleRespond(alert?.id)}
                        disabled={isProcessing}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-60"
                        style={{ background: '#f59e0b', color: '#fff' }}
                      >
                        {isProcessing ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Icon name="Phone" size={12} />
                            Respond
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleResolve(alert?.id)}
                      disabled={isProcessing}
                      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 disabled:opacity-60"
                      style={{ background: '#10b981', color: '#fff' }}
                    >
                      {isProcessing ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Icon name="CheckCircle" size={12} />
                          Resolve
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Resolved Alerts (collapsed) */}
        {resolvedAlerts?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-400 font-medium mb-2">Resolved ({resolvedAlerts?.length})</p>
            {resolvedAlerts?.slice(0, 3)?.map(alert => (
              <div key={alert?.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 mb-1.5">
                <Icon name="CheckCircle" size={14} className="text-green-500" />
                <span className="text-xs font-mono text-gray-500">{alert?.alert_number}</span>
                <span className="text-xs text-gray-500 truncate flex-1">{alert?.triggered_by_name}</span>
                <span className="text-xs text-green-600 font-medium">Resolved</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SOSPanel;
